// Intake weight + delivery sticker scan rules (spec #3956, mig 20261185000000).
//
// The SERVER owns the stored tare and net (deliveries_weight_snapshot); the
// form's preview below uses the same rungs so the number the clerk sees is the
// number that saves: the picked bin type's tare, else the business's
// system-default row for the enum container. No crate tare = no net (never the
// 37 kg bin fallback the tip resolver ends on).

import type { ContainerPickerValue } from '@/components/ContainerTypePicker'
import { isMixedRun } from '@/lib/linkedRuns'

interface TareRow { bin_type_id: string; code: string; tare_kg: number; is_system_default: boolean; tenant_id?: string | null }

/** A typed kg value: a positive number, or null for blank / not a number. */
export function parseKg(raw: string): number | null {
  const t = raw.trim().replace(',', '.')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** One crate's tare for the picker's value, as the server resolves it: an
 *  enum's system-default row prefers the business's own row over a global
 *  (tenant_id NULL) one, the order deliveries_weight_snapshot uses. */
export function crateTareKg(value: ContainerPickerValue, binTypes: readonly TareRow[]): number | null {
  const enumRows = value.startsWith('enum:')
    ? binTypes.filter(t => t.is_system_default && t.code === value.slice(5))
    : []
  const row = value.startsWith('type:')
    ? binTypes.find(t => t.bin_type_id === value.slice(5))
    : enumRows.find(t => t.tenant_id != null) ?? enumRows[0]
  return row && row.tare_kg > 0 ? row.tare_kg : null
}

export interface IntakeNet { containerTareKg: number; netKg: number }

/** Crate tare x crates, and gross - that - other tare. Null when any input is missing. */
export function intakeNet(a: { grossKg: number | null; otherTareKg: number | null; crateTareKg: number | null; crates: number }): IntakeNet | null {
  if (a.grossKg == null || a.grossKg <= 0 || a.crateTareKg == null || !(a.crates >= 1)) return null
  const containerTareKg = Math.round(a.crateTareKg * a.crates * 1000) / 1000
  const netKg = Math.round((a.grossKg - containerTareKg - (a.otherTareKg ?? 0)) * 100) / 100
  return { containerTareKg, netKg }
}

/** The form's own refusals, worded for the clerk. Null = fine (weight is optional). */
export function intakeWeightProblem(a: { grossKg: number | null; grossRaw: string; otherTareKg: number | null; otherRaw: string; net: IntakeNet | null }): string | null {
  if (a.grossRaw.trim() && (a.grossKg == null || a.grossKg <= 0)) return 'Gross weight must be a number above 0 kg.'
  if (a.otherRaw.trim() && (a.otherTareKg == null || a.otherTareKg < 0)) return 'Pallet / other tare must be 0 kg or more.'
  if (a.otherTareKg != null && a.otherTareKg > 0 && a.grossKg == null) return 'Type the gross weight too, or clear the pallet tare.'
  if (a.net && a.net.netKg < 0) return `The tare (${a.net.containerTareKg + (a.otherTareKg ?? 0)} kg) is more than the gross weight. Check the weight, the crate type and the number of crates.`
  return null
}

/** Tipping's "Use intake weight": the delivery's crates + fruit as weighed at
 *  intake (gross less the pallet), offered only for one tip of EVERY crate of
 *  an untouched delivery — after a part-tip the intake gross no longer matches
 *  what is left. Never when the server has no net: crates joined after the
 *  weigh (the gross covers only some of them), or the crate has no tare. */
export function intakeTipGrossKg(
  d: { gross_weight_kg?: number | null; other_tare_kg?: number | null; net_weight_kg?: number | null; bins_received: number },
  binQty: number, binsRemaining: number,
): number | null {
  if (d.gross_weight_kg == null || d.net_weight_kg == null) return null
  if (binsRemaining !== d.bins_received || binQty !== binsRemaining) return null
  const kg = Math.round((Number(d.gross_weight_kg) - Number(d.other_tare_kg ?? 0)) * 100) / 100
  return kg > 0 ? kg : null
}

// ---------------------------------------------------------------------------
// Scan a delivery sticker on the Tipping board (spec §4).
// ---------------------------------------------------------------------------

export interface ScannedDelivery {
  delivery_id:    string
  grower:         string | null
  grower_id:      string | null
  variety:        string | null
  /** Linked runs: a run of every variety (MIXED) checks the fruit, not the variety. */
  commodity_id?:  string | null
  status:         string
  bins_remaining: number
  delivery_note_number?: string | null
}

export type DeliveryScanDecision<D> =
  | { kind: 'open'; delivery: D }
  | { kind: 'add'; delivery: D }
  | { kind: 'lookup' }
  | { kind: 'refuse'; title: string; message: string }

const same = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()

/** What a scanned sticker does. `onRun` is the board's own list (this run's
 *  deliveries with crates left); `looked` is the server's answer for anything
 *  not on it: undefined = not asked yet, 'error' = no answer, null = no such
 *  delivery in this business. One rule per outcome, all spoken out. */
// One early return per scan outcome; each is pinned by a case in intakeWeight.test.ts.
// fallow-ignore-next-line complexity
export function decideDeliveryScan<D extends ScannedDelivery>(a: {
  code: string
  run: { grower: string | null; grower_id?: string | null; variety: string | null; run_number: string; run_kind?: string | null; commodity_id?: string | null } | null
  onRun: readonly D[]
  deliveryId: string
  looked?: D | null | 'error'
}): DeliveryScanDecision<D> {
  if (!a.run) return { kind: 'refuse', title: 'Pick a run first', message: `Scanned ${a.code} — select the destination run, then scan the delivery sticker again.` }
  const mine = a.onRun.find(d => d.delivery_id === a.deliveryId)
  if (mine) return { kind: 'open', delivery: mine }
  if (a.looked === undefined) return { kind: 'lookup' }
  if (a.looked === 'error') return { kind: 'refuse', title: "Couldn't look up the delivery", message: `${a.code}: check the connection and scan again.` }
  if (a.looked === null) return { kind: 'refuse', title: 'No such delivery', message: `${a.code} isn't a delivery on this site.` }
  const d = a.looked
  const dn = d.delivery_note_number ? ` (DN ${d.delivery_note_number})` : ''
  if (d.bins_remaining <= 0) return { kind: 'refuse', title: 'Already tipped', message: `This delivery${dn} is already fully tipped.` }
  const growerOff = a.run.grower_id && d.grower_id ? a.run.grower_id !== d.grower_id : !same(d.grower, a.run.grower)
  // A run of every variety (linked runs, mig 20261288000000) takes any variety
  // of its own fruit.
  const fruitOff = isMixedRun(a.run)
    ? !!(d.commodity_id && a.run.commodity_id && d.commodity_id !== a.run.commodity_id)
    : !same(d.variety, a.run.variety)
  if (growerOff || fruitOff) {
    return { kind: 'refuse', title: "Not this run's fruit",
      message: `This delivery${dn} is ${d.grower ?? '—'} · ${d.variety ?? '—'}. ${a.run.run_number} is ${a.run.grower ?? '—'} · ${a.run.variety ?? '—'}. Pick another run.` }
  }
  if (d.status !== 'IN_STOCK' && d.status !== 'RECEIVED') {
    return { kind: 'refuse', title: 'Not ready to tip',
      message: `This delivery${dn} is ${d.status.replace(/_/g, ' ').toLowerCase()}. Only an In Stock or Received delivery can go on a run.` }
  }
  return { kind: 'add', delivery: d }
}
